import { useCallback } from "react";

import { usePrettyLogEventPage } from "@components/v2/analytics";

import { type TrackedComponent } from "./components";
import {
  type CreateCustomBudgetMMMEvent,
  type ExploreBudgetsMMMEvent,
  MMMTab,
  type ModelInsightsMMMEvent,
  type RecapPerformanceMMMEvent,
  type TrackPerformanceMMMEvent,
  type MMMEvent,
  type CommonComponentMMMEvent,
  type CreateMediaPlanMMMEvent,
} from "./mmm-event";

type FullMMMEventCallback = (mmmEvent: MMMEvent) => void;
type ExploreBudgetsComponentCallback = (
  mmmEvent: ExploreBudgetsMMMEvent,
) => void;
type ModelInsightsComponentCallback = (mmmEvent: ModelInsightsMMMEvent) => void;
type CreateCustomBudgetComponentCallback = (
  mmmEvent: CreateCustomBudgetMMMEvent,
) => void;
type TrackPerformanceComponentCallback = (
  mmmEvent: TrackPerformanceMMMEvent,
) => void;
type RecapPerformanceComponentCallback = (
  mmmEvent: RecapPerformanceMMMEvent,
) => void;

type CreateMediaPlanComponentCallback = (
  mmmEvent: CreateMediaPlanMMMEvent,
) => void;

type CommonComponentCallback = (mmmEvent: CommonComponentMMMEvent) => void;

type MMMEventTracker = <T extends MMMTab | undefined = undefined>(
  tab?: T,
) => T extends undefined
  ? FullMMMEventCallback
  : T extends MMMTab.EXPLORE_BUDGETS
  ? ExploreBudgetsComponentCallback
  : T extends MMMTab.MODEL_INSIGHTS
  ? ModelInsightsComponentCallback
  : T extends MMMTab.CREATE_CUSTOM_BUDGETS
  ? CreateCustomBudgetComponentCallback
  : T extends MMMTab.TRACK_PERFORMANCE
  ? TrackPerformanceComponentCallback
  : T extends MMMTab.RECAP_PERFORMANCE
  ? RecapPerformanceComponentCallback
  : T extends MMMTab.CREATE_MEDIA_PLAN
  ? CreateMediaPlanComponentCallback
  : CommonComponentCallback;

type AnyMMMEvent =
  | MMMEvent
  | ExploreBudgetsMMMEvent
  | ModelInsightsMMMEvent
  | CreateCustomBudgetMMMEvent
  | TrackPerformanceMMMEvent
  | RecapPerformanceMMMEvent
  | CreateMediaPlanMMMEvent
  | CommonComponentMMMEvent;

export const useTrackMMMEvent = (<T extends MMMTab | undefined>(tab?: T) => {
  const prettyLogEventPage = usePrettyLogEventPage();

  const createDomain = useCallback(
    (tabForDomain: MMMTab) => `MMM: ${tabForDomain}`,
    [],
  );

  const createInfo = useCallback(
    (component: TrackedComponent, actionInfo?: string) =>
      actionInfo ? `${component}: ${actionInfo}` : component,
    [],
  );

  const prettyLogMMMEvent = useCallback(
    (mmmEvent: AnyMMMEvent) => {
      const mmmTab = tab ? tab : (mmmEvent as MMMEvent).tab;
      prettyLogEventPage({
        domain: createDomain(mmmTab),
        action: mmmEvent.action,
        additionalActionInfo: createInfo(
          mmmEvent.component,
          mmmEvent.actionInfo,
        ),
        data: mmmEvent.data,
      });
    },
    [prettyLogEventPage, createDomain, createInfo, tab],
  );

  if (tab === undefined) {
    return useCallback(
      (mmmEvent: MMMEvent) => {
        prettyLogMMMEvent(mmmEvent);
      },
      [prettyLogMMMEvent],
    );
  } else {
    switch (tab) {
      case MMMTab.EXPLORE_BUDGETS:
        return useCallback(
          (mmmEvent: ExploreBudgetsMMMEvent) => {
            prettyLogMMMEvent(mmmEvent);
          },
          [prettyLogMMMEvent],
        );
      case MMMTab.MODEL_INSIGHTS:
        return useCallback(
          (mmmEvent: ModelInsightsMMMEvent) => {
            prettyLogMMMEvent(mmmEvent);
          },
          [prettyLogMMMEvent],
        );
      case MMMTab.CREATE_CUSTOM_BUDGETS:
        return useCallback(
          (mmmEvent: CreateCustomBudgetMMMEvent) => {
            prettyLogMMMEvent(mmmEvent);
          },
          [prettyLogMMMEvent],
        );
      case MMMTab.TRACK_PERFORMANCE:
        return useCallback(
          (mmmEvent: TrackPerformanceMMMEvent) => {
            prettyLogMMMEvent(mmmEvent);
          },
          [prettyLogMMMEvent],
        );
      case MMMTab.RECAP_PERFORMANCE:
        return useCallback(
          (mmmEvent: RecapPerformanceMMMEvent) => {
            prettyLogMMMEvent(mmmEvent);
          },
          [prettyLogMMMEvent],
        );
      case MMMTab.CREATE_MEDIA_PLAN:
        return useCallback(
          (mmmEvent: CreateMediaPlanMMMEvent) => {
            prettyLogMMMEvent(mmmEvent);
          },
          [prettyLogMMMEvent],
        );
      case MMMTab.ALL_TABS:
        return useCallback(
          (mmmEvent: CommonComponentMMMEvent) => {
            prettyLogMMMEvent(mmmEvent);
          },
          [prettyLogMMMEvent],
        );

      default:
        throw new Error("Unknown MMM Tab");
    }
  }
}) as MMMEventTracker;
